import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import api from "@/services/api";
import VideoPlayer from "@/components/base/VideoPlayer";

interface ProjectItem {
  id: string;
  title: string;
  description: string;
  stage: string;
  progress: number;
  tech_stack: string[];
  team: string[];
  collaborators: string[];
  updates: { id: string; author_name: string; author_role: string; message: string; created_at: string }[];
  thumbnail_url?: string;
  video_url?: string;
  video_type?: string;
  featured?: boolean;
  screenshots?: string[];
  client?: { company_name: string };
  start_date?: string;
  due_date?: string;
  budget?: string;
  spent?: string;
}



export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<ProjectItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeScreenshotIndex, setActiveScreenshotIndex] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/projects/public/${id}`)
      .then((res) => setProject(res.data.data))
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [id]);

  const tech = Array.isArray(project?.tech_stack) ? project.tech_stack : [];
  const team = Array.isArray(project?.team) ? project.team : [];
  const collaborators = Array.isArray(project?.collaborators) ? project.collaborators : [];
  const updates = Array.isArray(project?.updates) ? project.updates : [];



  const convertDriveLink = (url: string) => {
    console.log('Converting URL:', url);
    if (url.includes('drive.google.com')) {
      let fileId = null;

      const fileMatch = url.match(/\/file\/d\/([^\/]+)/);
      if (fileMatch) {
        fileId = fileMatch[1];
        console.log('Matched file format, fileId:', fileId);
      } else {
        const idMatch = url.match(/[?&]id=([^&]+)/);
        if (idMatch) {
          fileId = idMatch[1];
          console.log('Matched id format, fileId:', fileId);
        }
      }

      if (fileId) {
        const converted = `https://drive.google.com/thumbnail?id=${fileId}&sz=w2000`;
        console.log('Converted to:', converted);
        return converted;
      } else {
        console.log('No fileId found');
      }
    } else {
      console.log('Not a Google Drive URL');
    }
    return url;
  };

  const thumbnail = convertDriveLink(project?.thumbnail_url || "");
  const videoUrl = project?.video_url || "";
  const videoType = (project?.video_type === "direct" ? "upload" : project?.video_type) as "youtube" | "vimeo" | "embed" | "upload";
  const clientName = project?.client?.company_name || "";
  const startDate = project?.start_date ? new Date(project.start_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
  const dueDate = project?.due_date ? new Date(project.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-black font-inter">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-deep-black font-inter">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <i className="ri-error-warning-line text-4xl text-cyber-orange mb-3 block" />
            <h2 className="text-white font-bold text-xl mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Project Not Found</h2>
            <p className="text-silver mb-6">The project you're looking for doesn't exist or is not publicly available.</p>
            <Link to="/projects" className="inline-flex items-center gap-2 px-6 py-3 bg-electric-blue text-deep-black font-semibold rounded-xl hover:shadow-glow-blue transition-all">
              <i className="ri-arrow-left-line" /> Back to Projects
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black font-inter">
      <Navbar />

      <section className="relative pt-32 pb-20 px-4 md:px-12 lg:px-20 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-electric-blue/5 via-transparent to-transparent opacity-60" />
          <div className="absolute inset-0 bg-circuit-pattern opacity-20" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          {/* Back Button */}
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-silver hover:text-white mb-8 transition-colors"
          >
            <i className="ri-arrow-left-line" /> Back to Projects
          </Link>

          {/* Hero Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs px-3 py-1.5 rounded-full bg-electric-blue/10 border border-electric-blue/20 text-electric-blue font-medium">
                  {project.stage.charAt(0).toUpperCase() + project.stage.slice(1)}
                </span>
                {project.featured && (
                  <span className="text-xs px-3 py-1.5 rounded-full bg-cyber-green/10 border border-cyber-green/20 text-cyber-green font-medium">
                    Featured
                  </span>
                )}
              </div>
              <h1 className="text-white font-black text-4xl lg:text-5xl mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {project.title}
              </h1>
              <p className="text-silver text-lg leading-relaxed mb-8">
                {project.description || "No description available."}
              </p>

              {/* Project Stats */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-dark-card border border-dark-border rounded-xl p-4">
                  <span className="text-silver-dim text-xs uppercase tracking-wider">Progress</span>
                  <p className="text-white text-lg font-semibold mt-1">{project.progress}%</p>
                </div>
                <div className="bg-dark-card border border-dark-border rounded-xl p-4">
                  <span className="text-silver-dim text-xs uppercase tracking-wider">Client</span>
                  <p className="text-white text-lg font-semibold mt-1">{clientName || "N/A"}</p>
                </div>
              </div>

              {/* Tech Stack */}
              <div className="mb-8">
                <h3 className="text-white font-semibold text-sm mb-3">Tech Stack</h3>
                <div className="flex flex-wrap gap-2">
                  {tech.map((t) => (
                    <span key={t} className="text-xs px-3 py-1.5 rounded-lg bg-deep-black border border-dark-border text-silver">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div>
              {/* Media */}
              {videoUrl ? (
                <VideoPlayer url={videoUrl} type={videoType} className="rounded-2xl overflow-hidden mb-6" />
              ) : thumbnail ? (
                <img
                  src={thumbnail}
                  alt={project.title}
                  referrerPolicy="no-referrer"
                  className="w-full aspect-video object-cover rounded-2xl mb-6"
                />
              ) : null}
            </div>
          </div>

          {Array.isArray(project.screenshots) && project.screenshots.length > 0 && (
            <div className="mb-16">
              <h2 className="text-white font-bold text-2xl mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Screenshots</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {project.screenshots.map((url, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setActiveScreenshotIndex(idx); setLightboxOpen(true); }}
                    className="block w-full text-left rounded-2xl border border-dark-border hover:border-electric-blue/50 transition-colors cursor-pointer overflow-hidden"
                  >
                    <img
                      src={convertDriveLink(url)}
                      alt={`Screenshot ${idx + 1}`}
                      referrerPolicy="no-referrer"
                      className="w-full aspect-video object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Project Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="space-y-6">
              <h2 className="text-white font-bold text-2xl mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Project Details</h2>

              {startDate && (
                <div>
                  <span className="text-silver-dim text-xs uppercase tracking-wider">Start Date</span>
                  <p className="text-white text-sm mt-1">{startDate}</p>
                </div>
              )}

              {dueDate && (
                <div>
                  <span className="text-silver-dim text-xs uppercase tracking-wider">Due Date</span>
                  <p className="text-white text-sm mt-1">{dueDate}</p>
                </div>
              )}

              {project.budget && (
                <div>
                  <span className="text-silver-dim text-xs uppercase tracking-wider">Budget</span>
                  <p className="text-white text-sm mt-1">{project.budget}</p>
                </div>
              )}


            </div>

            <div className="space-y-6">
              <h3 className="text-white font-semibold text-lg mb-4">Team</h3>
              <div className="flex flex-wrap gap-3">
                {team.map((member) => (
                  <div key={member} className="flex items-center gap-3 px-4 py-2 rounded-xl bg-dark-card border border-dark-border">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-electric-blue/20 text-electric-blue text-sm font-bold">
                      {member.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <span className="text-silver text-sm">{member}</span>
                  </div>
                ))}
              </div>

              {collaborators.length > 0 && (
                <>
                  <h3 className="text-white font-semibold text-lg mb-4">Collaborators</h3>
                  <div className="flex flex-wrap gap-2">
                    {collaborators.map((collab) => (
                      <span key={collab} className="text-xs px-3 py-1.5 rounded-lg bg-deep-black border border-dark-border text-silver">
                        {collab}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Updates */}
          {updates.length > 0 && (
            <div className="mb-16">
              <h2 className="text-white font-bold text-2xl mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Project Updates</h2>
              <div className="space-y-4">
                {updates.map((update) => (
                  <div key={update.id} className="flex gap-4 p-4 rounded-xl bg-dark-card border border-dark-border">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-electric-blue/20 text-electric-blue text-sm font-bold flex-shrink-0">
                      {update.author_name.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-white text-sm font-semibold">{update.author_name}</span>
                        <span className="text-silver-dim text-xs">{update.author_role}</span>
                        <span className="text-silver-dim text-xs">{new Date(update.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-silver text-sm leading-relaxed">{update.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="text-center">
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 px-8 py-4 bg-electric-blue text-deep-black font-bold rounded-xl hover:shadow-glow-blue transition-all"
            >
              View More Projects <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Screenshot Lightbox */}
      {lightboxOpen && Array.isArray(project.screenshots) && project.screenshots.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <div
            className="relative max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center rounded-full bg-dark-card border border-dark-border text-silver hover:text-white cursor-pointer z-10"
            >
              <i className="ri-close-line text-lg" />
            </button>

            <img
              src={convertDriveLink(project.screenshots[activeScreenshotIndex])}
              alt={`Screenshot ${activeScreenshotIndex + 1}`}
              referrerPolicy="no-referrer"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const container = e.currentTarget.parentElement;
                if (container) {
                  const placeholder = document.createElement('div');
                  placeholder.className = 'flex items-center justify-center h-96 bg-dark-card rounded-2xl';
                  placeholder.innerHTML = '<div class="text-center text-silver-dim"><i class="ri-image-line text-4xl mb-2 block"></i><span>Image not available</span></div>';
                  container.appendChild(placeholder);
                }
              }}
              className="w-full rounded-2xl object-cover max-h-[70vh]"
            />

            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => setActiveScreenshotIndex((prev) => (prev - 1 + project.screenshots.length) % project.screenshots.length)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-dark-card border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 cursor-pointer transition-all"
              >
                <i className="ri-arrow-left-line" />
              </button>
              <span className="text-silver-dim text-sm">
                {activeScreenshotIndex + 1} / {project.screenshots.length}
              </span>
              <button
                onClick={() => setActiveScreenshotIndex((prev) => (prev + 1) % project.screenshots.length)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-dark-card border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 cursor-pointer transition-all"
              >
                <i className="ri-arrow-right-line" />
              </button>
            </div>

            <div className="flex gap-3 mt-4 justify-center overflow-x-auto">
              {project.screenshots.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveScreenshotIndex(i)}
                  className={`flex-shrink-0 w-16 h-10 rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                    i === activeScreenshotIndex ? "border-electric-blue" : "border-transparent hover:border-electric-blue/40"
                  }`}
                >
                  <img
                    src={convertDriveLink(img)}
                    alt=""
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div class="flex items-center justify-center w-full h-full bg-dark-card"><i class="ri-image-line text-silver-dim text-xs"></i></div>';
                      }
                    }}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}